import { useState, useMemo, useCallback } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { Add01Icon, Cancel01Icon } from '@hugeicons/core-free-icons'

import {
  useAdminAllOrganizations,
  useAdminCreateOrganization,
} from '@/features/admin/services/admin.convex'
import { AuthGuard } from '@/features/auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/components/ui/multi-select'

export const Route = createFileRoute('/(dashboard)/admin/organizations/')({
  component: RouteComponent,
  beforeLoad: () => ({
    title: 'Admin — Organizations',
    breadcrumbs: [{ title: 'Admin' }, { title: 'Organizations' }],
  }),
})

const billingBadgeVariant: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  active: 'default',
  unpaid: 'secondary',
  cancelled: 'outline',
  expired: 'destructive',
  past_due: 'destructive',
  paused: 'secondary',
}

function RouteComponent() {
  const { data: orgs, isLoading } = useAdminAllOrganizations()
  const createOrg = useAdminCreateOrganization()

  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [billingFilter, setBillingFilter] = useState<string[]>([])
  const [createOpen, setCreateOpen] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgDesc, setNewOrgDesc] = useState('')

  const hasActiveFilters =
    search.length > 0 || typeFilter.length > 0 || billingFilter.length > 0

  const clearFilters = useCallback(() => {
    setSearch('')
    setTypeFilter([])
    setBillingFilter([])
  }, [])

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    return orgs.filter((o) => {
      if (
        term &&
        !o.name.toLowerCase().includes(term) &&
        !(o.description ?? '').toLowerCase().includes(term)
      )
        return false
      if (
        typeFilter.length > 0 &&
        !typeFilter.includes(o.isAdmin ? 'platform' : 'customer')
      )
        return false
      if (
        billingFilter.length > 0 &&
        !o.isAdmin &&
        !billingFilter.includes(o.billingStatus)
      )
        return false
      return true
    })
  }, [orgs, search, typeFilter, billingFilter])

  const handleCreate = async () => {
    if (!newOrgName.trim()) return
    try {
      await createOrg({
        name: newOrgName.trim(),
        description: newOrgDesc.trim() || undefined,
      })
      toast.success(`Organization "${newOrgName}" created`)
      setCreateOpen(false)
      setNewOrgName('')
      setNewOrgDesc('')
    } catch {
      toast.error('Failed to create organization')
    }
  }

  return (
    <AuthGuard requiresPlatformAdmin>
      <div className="space-y-6">
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Organizations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isLoading
                ? 'Loading…'
                : `${orgs.length} organization${orgs.length !== 1 ? 's' : ''} total`}
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <HugeiconsIcon icon={Add01Icon} className="mr-2 h-4 w-4" />
            New Organization
          </Button>
        </div>

        <div className="bg-muted/30 rounded-lg p-6">
          <div className="relative w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/50 border-b transition-colors">
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Name
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Type
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Members
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Seats
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Billing
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Actions
                  </TableHead>
                </TableRow>
                {/* Filter row */}
                <TableRow className="border-b bg-muted/20 hover:bg-muted/20">
                  <TableHead className="px-2 py-1.5">
                    <Input
                      placeholder="Search…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-8 bg-background text-sm font-normal"
                    />
                  </TableHead>
                  <TableHead className="px-2 py-1.5">
                    <MultiSelect
                      values={typeFilter}
                      onValuesChange={setTypeFilter}
                    >
                      <MultiSelectTrigger className="h-8 w-full bg-background text-sm font-normal">
                        <MultiSelectValue placeholder="All types…" />
                      </MultiSelectTrigger>
                      <MultiSelectContent>
                        <MultiSelectGroup>
                          <MultiSelectItem value="platform">
                            Platform
                          </MultiSelectItem>
                          <MultiSelectItem value="customer">
                            Customer
                          </MultiSelectItem>
                        </MultiSelectGroup>
                      </MultiSelectContent>
                    </MultiSelect>
                  </TableHead>
                  <TableHead className="px-2 py-1.5" />
                  <TableHead className="px-2 py-1.5" />
                  <TableHead className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <MultiSelect
                        values={billingFilter}
                        onValuesChange={setBillingFilter}
                      >
                        <MultiSelectTrigger className="h-8 flex-1 bg-background text-sm font-normal">
                          <MultiSelectValue placeholder="All statuses…" />
                        </MultiSelectTrigger>
                        <MultiSelectContent>
                          <MultiSelectGroup>
                            {Object.keys(billingBadgeVariant).map((s) => (
                              <MultiSelectItem key={s} value={s}>
                                {s
                                  .replace('_', ' ')
                                  .replace(/^\w/, (c) => c.toUpperCase())}
                              </MultiSelectItem>
                            ))}
                          </MultiSelectGroup>
                        </MultiSelectContent>
                      </MultiSelect>
                    </div>
                  </TableHead>
                  <TableHead className="px-2 py-1.5">
                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="h-8 w-8 p-0 text-muted-foreground shrink-0"
                      >
                        <HugeiconsIcon
                          icon={Cancel01Icon}
                          className="h-4 w-4"
                        />
                      </Button>
                    )}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow
                      key={i}
                      className="hover:bg-muted/50 border-b transition-colors"
                    >
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell
                          key={j}
                          className="p-2 align-middle whitespace-nowrap"
                        >
                          <div className="h-4 bg-muted animate-pulse rounded w-24" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!isLoading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="p-2 align-middle text-center text-muted-foreground py-12"
                    >
                      No organizations found
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  filtered.map((org) => (
                    <TableRow
                      key={org.id}
                      className="hover:bg-muted/50 border-b transition-colors"
                    >
                      <TableCell className="p-2 align-middle whitespace-nowrap">
                        <Link
                          to="/admin/organizations/$organizationId/members"
                          params={{ organizationId: org.id }}
                          search={{ name: org.name }}
                          className="font-medium hover:underline"
                        >
                          {org.name}
                        </Link>
                        {org.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-48 mt-0.5">
                            {org.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap">
                        <Badge variant={org.isAdmin ? 'default' : 'secondary'}>
                          {org.isAdmin ? 'Platform' : 'Customer'}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-sm">
                        {org.memberCount} / {org.employeeLimit}
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-sm">
                        {org.paidQuantity > 0
                          ? `${org.paidQuantity} paid`
                          : 'Free tier'}
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap">
                        {!org.isAdmin && (
                          <Badge
                            variant={
                              billingBadgeVariant[org.billingStatus] ??
                              'secondary'
                            }
                          >
                            {org.billingStatus.replace('_', ' ')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap">
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to="/admin/organizations/$organizationId/members"
                            params={{ organizationId: org.id }}
                            search={{ name: org.name }}
                          >
                            View Organization
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                placeholder="Acme Corp"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-desc">Description (optional)</Label>
              <Input
                id="org-desc"
                placeholder="Short description…"
                value={newOrgDesc}
                onChange={(e) => setNewOrgDesc(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!newOrgName.trim()}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AuthGuard>
  )
}
